// Three-tier consent manager for Loi 2008-12 compliance.
// Tiers: essential (always on), functional (default on, opt-out),
//        analytics (default off, opt-in), marketing (default off, future).
//
// Integration points:
// - Tier 0 (essential): cart, session, CSRF — always active
// - Tier 1 (functional): FingerprintJS, Supabase events, GA4 page views
// - Tier 2 (analytics): PostHog session replays, heatmaps, FB Pixel
// - Tier 3 (marketing): retargeting pixels — future

const CONSENT_KEY = "djayma_consent";
export const CONSENT_VERSION = "2.1";

export type ConsentTier = "essential" | "functional" | "analytics" | "marketing";

export interface ConsentState {
  readonly version: string;
  readonly essential: true; // always true — cannot be disabled
  readonly functional: boolean;
  readonly analytics: boolean;
  readonly marketing: boolean;
  readonly timestamp: string | null;
}

interface ConsentInput {
  readonly functional: boolean;
  readonly analytics: boolean;
  readonly marketing: boolean;
}

const DEFAULT_CONSENT: ConsentState = {
  version: CONSENT_VERSION,
  essential: true,
  functional: true, // default ON — legitimate interest basis
  analytics: false, // default OFF — requires explicit consent
  marketing: false, // default OFF — future
  timestamp: null,
};

function readFromStorage(): ConsentState {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return DEFAULT_CONSENT;

    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (typeof parsed !== "object" || parsed === null) return DEFAULT_CONSENT;
    if (parsed.version !== CONSENT_VERSION) return DEFAULT_CONSENT;

    return {
      version: CONSENT_VERSION,
      essential: true,
      functional: typeof parsed.functional === "boolean" ? parsed.functional : true,
      analytics: typeof parsed.analytics === "boolean" ? parsed.analytics : false,
      marketing: typeof parsed.marketing === "boolean" ? parsed.marketing : false,
      timestamp: typeof parsed.timestamp === "string" ? parsed.timestamp : null,
    };
  } catch {
    return DEFAULT_CONSENT;
  }
}

function writeToStorage(state: ConsentState): void {
  localStorage.setItem(CONSENT_KEY, JSON.stringify(state));
}

function applySideEffects(state: ConsentState): void {
  // Tier 2 (analytics): control PostHog
  // Uses dynamic import to avoid circular dependency with posthog.ts
  import("@/lib/posthog").then(({ initPostHog, shutdownPostHog }) => {
    if (state.analytics) {
      initPostHog();
    } else {
      shutdownPostHog();
    }
  }).catch(() => {
    // PostHog module may not be available — safe to ignore
  });

  // Tier 2 (analytics): control Facebook Pixel
  import("@/lib/facebook-pixel").then(({ initFacebookPixel, shutdownFacebookPixel }) => {
    if (state.analytics) {
      initFacebookPixel();
    } else {
      shutdownFacebookPixel();
    }
  }).catch(() => {
    // Facebook Pixel module may not be available — safe to ignore
  });
}

// ─── Public API ─────────────────────────────────────────────────────

export function getConsent(): ConsentState {
  return readFromStorage();
}

export function setConsent(input: ConsentInput): ConsentState {
  const state: ConsentState = {
    version: CONSENT_VERSION,
    essential: true,
    functional: input.functional,
    analytics: input.analytics,
    marketing: input.marketing,
    timestamp: new Date().toISOString(),
  };
  writeToStorage(state);
  applySideEffects(state);
  return state;
}

export function isConsentGiven(tier: ConsentTier): boolean {
  const consent = readFromStorage();
  switch (tier) {
    case "essential":
      return true;
    case "functional":
      return consent.functional;
    case "analytics":
      return consent.analytics;
    case "marketing":
      return consent.marketing;
    default:
      return false;
  }
}

export function revokeConsent(tier: ConsentTier): ConsentState {
  if (tier === "essential") return readFromStorage(); // cannot revoke essential
  const current = readFromStorage();
  const updated: ConsentState = {
    ...current,
    [tier]: false,
    timestamp: new Date().toISOString(),
  };
  writeToStorage(updated);
  applySideEffects(updated);
  return updated;
}

export function acceptAll(): ConsentState {
  return setConsent({ functional: true, analytics: true, marketing: true });
}

export function rejectAll(): ConsentState {
  return setConsent({ functional: false, analytics: false, marketing: false });
}

export function hasUserMadeChoice(): boolean {
  const consent = readFromStorage();
  return consent.timestamp !== null;
}

// ─── Backward-compatible exports (Plan A) ───────────────────────────

export const CONSENT_STORAGE_KEY = CONSENT_KEY;
export const CONSENT_TEXT_VERSION = CONSENT_VERSION;

export function getConsentPreferences(): ConsentState | null {
  const consent = readFromStorage();
  return consent.timestamp !== null ? consent : null;
}

export function setConsentPreferences(
  prefs: Omit<ConsentState, "version" | "timestamp" | "essential">,
  method: "banner" | "footer_toggle" | "settings_page",
  visitorId: string | null
): void {
  const state = setConsent({
    functional: prefs.functional,
    analytics: prefs.analytics,
    marketing: prefs.marketing,
  });

  // Sync each consent type to the API (fire-and-forget)
  const API_URL = import.meta.env.VITE_API_URL || "";
  const consentTypes: Array<"functional" | "analytics" | "marketing"> = [
    "functional",
    "analytics",
    "marketing",
  ];

  for (const type of consentTypes) {
    const status = state[type] ? "granted" : "denied";
    const endpoint = `${API_URL}/api/analytics/consent`;
    fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        visitor_id: visitorId,
        consent_type: type,
        status,
        method,
        consent_text_version: CONSENT_VERSION,
      }),
    }).catch(() => {
      // Non-blocking — consent is stored locally, API sync is best-effort
    });
  }
}

export function hasAnalyticsConsent(): boolean {
  return isConsentGiven("analytics");
}

export function hasMarketingConsent(): boolean {
  return isConsentGiven("marketing");
}
